/**
 * AI Editor Report — PDF export.
 *
 * Builds a print-ready HTML document that mirrors the Claude Design comp
 * "AI Editor Report - PDF Export.dc.html" (Letter, 0.72in margin, Lato,
 * BetaReader teal/orange palette, per-page footer) and opens the browser's
 * print dialog so the author can save it as a PDF. Vector + selectable text +
 * real CSS page-breaks, which raster html→canvas approaches can't give.
 *
 * The design's mock had a per-item "Suggested fix"; the real report schema is
 * diagnostic (no per-item fixes), so we map to the fields that exist and reuse
 * the design's visual system for every real section (incl. Act structure and
 * "Fix before the next version", which the markdown export also carried).
 */
import {
  EditorFeedbackReport,
  EditorClassification,
} from 'src/hooks/useEditorFeedback';

type TFunc = (key: string, def?: string, opts?: Record<string, unknown>) => string;

export type EditorReportPdfParams = {
  report: EditorFeedbackReport;
  classification?: EditorClassification;
  bookTitle: string;
  creditsCharged?: number;
  completedAt?: string;
  versionNumber?: number;
  chapterCount?: number;
  wordCount?: number;
  t: TFunc;
};

const esc = (s: unknown): string =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Severity chip palette — verbatim from the design comp's `S` map.
const SEV = {
  High: { c: '#B23A2E', bg: '#FBEDEB' },
  Medium: { c: '#A9700C', bg: '#FBF1E2' },
  Low: { c: '#2C7F78', bg: '#E6F3F1' },
  Setup: { c: '#1D5F5A', bg: '#E6F3F1' },
} as const;
type SevKey = keyof typeof SEV;

const chip = (label: string, k: SevKey): string =>
  `<span style="display:inline-block;padding:2px 8px;border-radius:4px;font:800 9.5px 'Lato';line-height:1.5;letter-spacing:.07em;text-transform:uppercase;color:${SEV[k].c};background:${SEV[k].bg};white-space:nowrap">${esc(label)}</span>`;

const sevKeyFor = (s: 'critical' | 'major' | 'minor'): SevKey =>
  s === 'critical' ? 'High' : s === 'major' ? 'Medium' : 'Low';

const formatCh = (ch: string | number): string => {
  const str = String(ch).trim();
  return /^\d+$/.test(str) ? `Ch. ${str}` : str;
};

const sectionHeader = (label: string, count?: number): string =>
  `<div style="display:flex;align-items:center;gap:9px;border-bottom:2px solid #0E3B40;padding-bottom:5px;break-after:avoid">` +
  `<span style="font:800 12px 'Lato';letter-spacing:.14em;text-transform:uppercase;color:#0E3B40">${esc(label)}</span>` +
  (count != null
    ? `<span style="display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:#0E3B40;color:#fff;font:800 10px 'Lato'">${count}</span>`
    : '') +
  `</div>`;

type Item = {
  num: string | number;
  title?: string;
  chip?: string;
  where?: string;
  desc?: string;
  secLabel?: string;
  secText?: string;
  titleSize?: string;
};

const itemRow = (it: Item): string =>
  `<div style="display:flex;gap:14px;padding:13px 0;border-top:1px solid rgba(0,0,0,.08);break-inside:avoid">` +
  `<div style="font:800 14px 'Lato';color:#cfcabf;width:22px;flex:none;text-align:right">${esc(it.num)}</div>` +
  `<div style="flex:1;min-width:0">` +
  `<div style="display:flex;align-items:center;gap:9px;flex-wrap:wrap">` +
  (it.title
    ? `<span style="font:700 ${it.titleSize || '14.5px'} 'Lato';color:#1b1b1b">${esc(it.title)}</span>`
    : '') +
  (it.chip || '') +
  (it.where
    ? `<span style="font:italic 400 12px 'Lato';color:#9a968f">${esc(it.where)}</span>`
    : '') +
  `</div>` +
  (it.desc
    ? `<p style="margin:6px 0 0;font:400 13px/1.6 'Lato';color:#40403d">${esc(it.desc)}</p>`
    : '') +
  (it.secText
    ? `<div style="margin-top:6px;display:flex;gap:8px;align-items:baseline">` +
      `<span style="font:800 9px 'Lato';letter-spacing:.1em;text-transform:uppercase;color:#E67E00;flex:none;padding-top:2px;white-space:nowrap">${esc(it.secLabel)}</span>` +
      `<span style="font:400 12.5px/1.55 'Lato';color:#5a574f">${esc(it.secText)}</span></div>`
    : '') +
  `</div></div>`;

export const buildHtml = (p: EditorReportPdfParams): string => {
  const { report, classification, bookTitle, creditsCharged, completedAt, t } = p;

  const plotHoles = report.plotHoles || [];
  const unusedSetups = report.unusedSetups || [];
  const continuityNotes = report.continuityNotes || [];
  const acts = report.acts || [];
  const fixFirst = report.fixFirst || [];
  const charGroups = report.characters || [];
  const nChar = charGroups.reduce((s, g) => s + (g.members?.length || 0), 0);

  // ---- title-block meta line ----
  const metaParts: string[] = [];
  if (p.versionNumber != null)
    metaParts.push(`${t('Version', 'Version')} ${p.versionNumber}`);
  if (completedAt) {
    const d = new Date(completedAt);
    if (!Number.isNaN(d.getTime()))
      metaParts.push(
        `${t('GeneratedLabel', 'Generated')} ${d.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}`
      );
  }
  if (p.chapterCount != null)
    metaParts.push(`${p.chapterCount} ${t('Chapters', 'chapters')}`);
  if (p.wordCount != null)
    metaParts.push(`${p.wordCount.toLocaleString()} ${t('Words', 'words')}`);

  const metaItems: string[] = [];
  if (classification?.category || classification?.genres?.length) {
    const voice = [classification?.category, (classification?.genres || [])[0]]
      .filter(Boolean)
      .join(' · ');
    metaItems.push(
      `<span><b style="color:#4a4841;font-weight:700">${esc(t('EditorVoice', 'Editor voice'))}</b> &nbsp;${esc(voice)}</span>`
    );
  }
  if (creditsCharged)
    metaItems.push(
      `<span><b style="color:#4a4841;font-weight:700">${esc(t('CreditsUsed', 'Credits used'))}</b> &nbsp;${creditsCharged}</span>`
    );

  // ---- sections ----
  const summarySection = report.summary
    ? `<div style="margin-top:28px;break-inside:avoid">${sectionHeader(t('SummarizingPicture', 'Summarizing picture'))}` +
      `<p style="margin:13px 0 0;font:400 13.5px/1.72 'Lato';color:#333">${esc(report.summary)}</p>` +
      (fixFirst.length
        ? `<div style="margin-top:14px;border-left:3px solid #E67E00;background:#fff8ef;border-radius:0 6px 6px 0;padding:11px 14px;break-inside:avoid">` +
          `<div style="font:800 9px 'Lato';letter-spacing:.12em;text-transform:uppercase;color:#E67E00">${esc(t('FixBeforeNextVersion', 'Fix before the next version'))}</div>` +
          fixFirst
            .map(
              f =>
                `<div style="display:flex;gap:9px;margin-top:7px"><span style="font:800 12px 'Lato';color:#E67E00;flex:none">${esc(f.n)}</span><span style="font:400 12.5px/1.55 'Lato';color:#5a574f">${esc(f.text)}</span></div>`
            )
            .join('') +
          `</div>`
        : '') +
      `</div>`
    : '';

  const plotSection = plotHoles.length
    ? `<div style="margin-top:30px">${sectionHeader(t('PlotHoles', 'Plot holes'), plotHoles.length)}` +
      plotHoles
        .map((h, i) => {
          const chs = Array.from(
            new Set((h.quotes || []).map(q => formatCh(q.ch)))
          ).join(', ');
          const firstQuote = (h.quotes || [])[0];
          return itemRow({
            num: h.rank ?? i + 1,
            title: h.title,
            chip: chip(t(`Sev_${sevKeyFor(h.severity)}`, sevKeyFor(h.severity)), sevKeyFor(h.severity)),
            where: chs || undefined,
            desc: h.detail,
            secLabel: firstQuote ? t('Evidence', 'Evidence') : undefined,
            secText: firstQuote ? `“${firstQuote.text}”` : undefined,
          });
        })
        .join('') +
      `</div>`
    : '';

  const actsSection = acts.length
    ? `<div style="margin-top:30px">${sectionHeader(t('ActStructure', 'Act structure'), acts.length)}` +
      acts
        .map((a, i) =>
          itemRow({
            num: i + 1,
            title: a.name,
            where: a.chapters ? formatCh(a.chapters) : undefined,
            desc: a.note,
          })
        )
        .join('') +
      `</div>`
    : '';

  const setupsSection = unusedSetups.length
    ? `<div style="margin-top:30px">${sectionHeader(t('UnusedSetups', 'Unused setups'), unusedSetups.length)}` +
      unusedSetups
        .map((s, i) =>
          itemRow({
            num: i + 1,
            title: s.title,
            chip: chip(t('Setup', 'Setup'), 'Setup'),
            where: formatCh(s.ch),
            desc: s.note,
          })
        )
        .join('') +
      `</div>`
    : '';

  const continuitySection = continuityNotes.length
    ? `<div style="margin-top:30px">${sectionHeader(t('ContinuityNotes', 'Continuity notes'), continuityNotes.length)}` +
      continuityNotes
        .map((c, i) =>
          itemRow({ num: i + 1, where: formatCh(c.ch), desc: c.text })
        )
        .join('') +
      `</div>`
    : '';

  const charsSection = nChar
    ? `<div style="margin-top:30px">${sectionHeader(t('Characters', 'Characters'), nChar)}` +
      `<div style="display:grid;grid-template-columns:1fr 1fr;gap:0 30px;margin-top:6px">` +
      charGroups
        .flatMap(g => (g.members || []).map(m => ({ m, role: g.role })))
        .map(({ m, role }) => {
          const note = m.motivation || m.arc || m.traits || m.cites || '';
          return (
            `<div style="padding:9px 0;border-top:1px solid rgba(0,0,0,.08);break-inside:avoid">` +
            `<div style="display:flex;align-items:baseline;gap:8px"><span style="font:700 13.5px 'Lato';color:#1b1b1b">${esc(m.name)}</span><span style="font:800 8.5px 'Lato';letter-spacing:.09em;text-transform:uppercase;color:#2C8C84">${esc(role)}</span></div>` +
            (note
              ? `<p style="margin:3px 0 0;font:400 12px/1.5 'Lato';color:#54524c">${esc(note)}</p>`
              : '') +
            `</div>`
          );
        })
        .join('') +
      `</div></div>`
    : '';

  const footer =
    `<div class="report-footer"><span style="font-weight:800;color:#8a877f">Beta<span style="color:#FF8C00">Reader</span> <span style="color:#b7b3aa">· ${esc(t('AIEditor', 'AI Editor'))}</span></span>` +
    `<span>${esc(bookTitle)}${p.versionNumber != null ? ` · ${esc(t('Version', 'Version'))} ${p.versionNumber}` : ''}</span></div>`;

  return (
    `<!DOCTYPE html><html><head><meta charset="utf-8">` +
    `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>` +
    `<link href="https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,400;0,700;0,900;1,400&display=swap" rel="stylesheet">` +
    `<title>${esc(bookTitle)} · ${esc(t('AIEditorReport', 'AI Editor Report'))}</title>` +
    `<style>` +
    `@page{size:letter;margin:0.72in}` +
    `html{-webkit-print-color-adjust:exact;print-color-adjust:exact}` +
    `body{margin:0;font-family:'Lato',system-ui,sans-serif;-webkit-font-smoothing:antialiased;color:#1b1b1b;padding-bottom:34px}` +
    `.report-footer{position:fixed;bottom:0;left:0;right:0;display:flex;justify-content:space-between;align-items:center;font:400 9.5px 'Lato';color:#a29e95;letter-spacing:.02em;border-top:1px solid rgba(0,0,0,.12);padding-top:6px;background:#fff}` +
    `@media screen{body{max-width:7.06in;margin:0 auto;padding:0.72in 0.72in 48px}}` +
    `</style></head><body>` +
    footer +
    // header
    `<div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:12px;border-bottom:1px solid rgba(0,0,0,.13)">` +
    `<div style="font:800 16px 'Lato';color:#1b1b1b;letter-spacing:-.2px">Beta<span style="color:#FF8C00">Reader</span><span style="color:#b7b3aa">.io</span></div>` +
    `<div style="display:flex;align-items:center;gap:8px"><span style="width:6px;height:6px;border-radius:50%;background:#2C8C84;display:inline-block"></span><span style="font:800 10px 'Lato';letter-spacing:.2em;text-transform:uppercase;color:#2C8C84">${esc(t('AIEditorReport', 'AI Editor Report'))}</span></div></div>` +
    `<p style="font-size:12px;color:#6f6c65">AI-generated editorial analysis</p>` +
    // title block
    `<div style="margin-top:26px">` +
    `<div style="font:800 9.5px 'Lato';letter-spacing:.18em;text-transform:uppercase;color:#a09c93">${esc(t('EditorsReport', "Editor's report"))}</div>` +
    `<h1 style="margin:7px 0 0;font:800 34px/1.04 'Lato';color:#141414;letter-spacing:-.6px">${esc(bookTitle)}</h1>` +
    (metaParts.length
      ? `<div style="margin-top:9px;font:400 13px 'Lato';color:#6f6c65">${esc(metaParts.join(' · '))}</div>`
      : '') +
    (metaItems.length
      ? `<div style="margin-top:12px;display:flex;flex-wrap:wrap;gap:6px 20px;font:400 12px 'Lato';color:#7a776f">${metaItems.join('')}</div>`
      : '') +
    `</div>` +
    // stat row
    `<div style="display:flex;margin-top:22px;border:1px solid rgba(0,0,0,.11);border-radius:8px;overflow:hidden;break-inside:avoid">` +
    [
      [plotHoles.length, t('PlotHoles', 'Plot holes')],
      [unusedSetups.length, t('UnusedSetups', 'Unused setups')],
      [nChar, t('Characters', 'Characters')],
      [continuityNotes.length, t('ContinuityNotes', 'Continuity notes')],
    ]
      .map(
        ([n, label], i) =>
          `<div style="flex:1;padding:12px 15px${i < 3 ? ';border-right:1px solid rgba(0,0,0,.08)' : ''}"><div style="font:800 21px 'Lato';color:#0E3B40">${n}</div><div style="font:700 10.5px 'Lato';color:#7a776f;margin-top:2px;letter-spacing:.02em">${esc(label)}</div></div>`
      )
      .join('') +
    `</div>` +
    summarySection +
    plotSection +
    actsSection +
    setupsSection +
    continuitySection +
    charsSection +
    `</body></html>`
  );
};

/**
 * Build the report HTML, load it in a hidden iframe, and open the print dialog
 * (→ Save as PDF). Resolves once print() has been invoked.
 */
export const downloadEditorReportPdf = (p: EditorReportPdfParams): void => {
  const html = buildHtml(p);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(iframe);

  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    cleanup();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  const win = iframe.contentWindow as Window & typeof globalThis;
  const fire = () => {
    try {
      win.focus();
      win.onafterprint = () => setTimeout(cleanup, 500);
      win.print();
    } catch (e) {
      cleanup();
    }
    // Fallback cleanup in case onafterprint never fires (some browsers).
    setTimeout(cleanup, 60000);
  };

  const fontsReady = (doc as Document & { fonts?: FontFaceSet }).fonts?.ready;
  if (fontsReady) {
    fontsReady.then(() => setTimeout(fire, 120)).catch(() => setTimeout(fire, 300));
  } else {
    setTimeout(fire, 400);
  }
};
